import {
  type AbstractTemplateInOut,
  type ConfigurationFunctionValueInOut,
  isConfigurationExpressionValueInOut,
  isLibraryInOut,
  isSimpleValueSetupInOut,
  isSubTemplateInOut,
  isSubTemplateValueSetupInOut,
  isTemplateValueSetupInOut,
  type SimpleValueSetupInOut,
  type SubTemplateInOut,
  type SubTemplateValueSetupInOut,
  type TemplateFieldValueInOut,
  type TemplateValueSetupInOut,
  type TemplateVariantInOut,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import { functionDeclaration } from '../../components/templateFieldsTable/functionHelpers'
import { type Enriched, isTemplateInOut } from '../types'

const mergeFields = <T extends Record<string, unknown>>({
  parent,
  child,
}: {
  parent: Optional<T>
  child: Optional<T>
}): Optional<T> => {
  if (!parent && !child) {
    return
  }

  const parentWithFallback = parent ?? ({} as T)

  return {
    ...parentWithFallback,
    ...Object.fromEntries(
      Object.entries(child ?? {}).map(([key, value]) => {
        let mergedValue = value

        if (notEmpty(mergedValue) && Array.isArray(mergedValue)) {
          mergedValue =
            key in parentWithFallback &&
            notEmpty(parentWithFallback[key]) &&
            Array.isArray(parentWithFallback[key])
              ? [
                  ...mergedValue,
                  ...(parentWithFallback[key] as typeof mergedValue),
                ]
              : mergedValue
        }

        return [key, mergedValue]
      }),
    ),
  } as T
}

const flattenSimpleValueSetup = <T extends SimpleValueSetupInOut>({
  parent,
  child,
}: {
  parent: Optional<Enriched<T>>
  child: Optional<Enriched<T>>
}): Optional<Enriched<T>> => {
  const isChildNotRelevant = child?.valueConfig?.value === 'notRelevant'
  const flattenedValue = isChildNotRelevant
    ? child
    : mergeFields({ parent, child })

  if (flattenedValue) {
    flattenedValue.dynamicFunctions = isChildNotRelevant
      ? child.dynamicFunctions
      : flattenFunctions({
          child: child?.dynamicFunctions?.filter(notEmpty),
          parent: parent?.dynamicFunctions?.filter(notEmpty),
        })
  }

  return flattenedValue
}

const variantKey = (
  variant: Enriched<TemplateVariantInOut>,
  isMappingTemplate: boolean,
) => (isMappingTemplate ? variant.key : variant.name) ?? ''

const flattenTemplateValueSetup = <T extends TemplateValueSetupInOut>({
  parent,
  child,
  isMappingTemplate,
}: {
  parent: Optional<Enriched<T>>
  child: Optional<Enriched<T>>
  isMappingTemplate: boolean
}): Optional<Enriched<T>> => {
  const isChildNotRelevant = child?.valueConfig?.value === 'notRelevant'
  if (!parent || isChildNotRelevant) {
    return child
  }

  if (!child) {
    return parent
  }

  const childVariantKeys = (child.variants ?? [])
    .filter(notEmpty)
    .map((variant) => variantKey(variant, isMappingTemplate))
    .filter(notEmpty)

  const mergedFields = mergeFields({ parent, child })

  return {
    ...mergedFields,
    variants: [
      ...(child.variants ?? []),
      ...(parent.variants ?? []).filter(
        (variant) =>
          notEmpty(variant) &&
          !childVariantKeys.includes(variantKey(variant, isMappingTemplate)),
      ),
    ],
  } as Enriched<T>
}

const flattenFunctions = <T extends ConfigurationFunctionValueInOut>({
  child,
  parent,
}: {
  child: Optional<Enriched<T>[]>
  parent: Optional<Enriched<T>[]>
}) => {
  if (!child) {
    return parent
  }

  if (!parent) {
    return child
  }

  const childFunctionSignatures = child.map((fn) =>
    functionDeclaration(fn.function ?? ''),
  )

  return [
    ...(child ?? []),
    ...parent.filter(
      (fn) =>
        !childFunctionSignatures.includes(
          functionDeclaration(fn.function ?? ''),
        ),
    ),
  ]
}

const flattenSubTemplateWithOriginValueSetup = <
  T extends SubTemplateValueSetupInOut,
>({
  parent,
  child,
}: {
  parent: Optional<Enriched<T>>
  child: Enriched<T>
}): Enriched<T> => {
  const isChildNotRelevant = child.valueConfig?.value === 'notRelevant'
  const allFlattenedChildVariants = new Map<
    string,
    Enriched<SubTemplateInOut>
  >()

  const parentVariantsForFlattening = isChildNotRelevant
    ? []
    : (parent?.variants ?? [])

  const allParentVariants = new Map(
    parentVariantsForFlattening
      .filter(notEmpty)
      .map((variant) => [variant.name, variant]),
  )

  const allChildVariants = new Map(
    (child?.variants ?? [])
      .filter(notEmpty)
      .map((variant) => [variant.name, variant]),
  )

  ;(child?.variants ?? []).filter(notEmpty).forEach((childVariant) =>
    flattenSubtemplateVariant({
      childVariant: childVariant as Enriched<SubTemplateInOut>,
      childVariants: allChildVariants as Map<
        string,
        Enriched<SubTemplateInOut>
      >,
      parentVariants: allParentVariants as Map<
        string,
        Enriched<SubTemplateInOut>
      >,
      flattened: allFlattenedChildVariants,
    }),
  )

  const flattenedChildVariants = Array.from(allFlattenedChildVariants.values())

  return {
    ...parent,
    ...child,
    origin: child?.origin ?? parent?.origin,
    variants: [
      ...flattenedChildVariants,
      ...Array.from(allParentVariants.values()).filter(
        (variant) => !allFlattenedChildVariants.has(variant.name ?? ''),
      ),
    ],
  } as Enriched<T>
}

const flattenSubtemplateVariant = <T extends SubTemplateInOut>({
  childVariant,
  childVariants,
  parentVariants,
  flattened,
}: {
  childVariant: Enriched<T>
  childVariants: Map<string, Enriched<T>>
  parentVariants: Map<string, Enriched<T>>
  flattened: Map<string, Enriched<T>>
}) => {
  const templateId = childVariant?.name

  const parentId = childVariant.parent?.value
  const isParentAChildVariant = parentId && childVariants.has(parentId)
  assertNotEmpty(templateId)

  if (isParentAChildVariant && !flattened.has(templateId)) {
    flattenSubtemplateVariant({
      childVariant: childVariants.get(parentId)!,
      childVariants,
      parentVariants,
      flattened,
    })
  }

  const templateToFlatten = isParentAChildVariant
    ? childVariants.get(parentId)
    : parentVariants.get(parentId ?? templateId)

  if (templateToFlatten && !flattened.has(templateId)) {
    const flattenedChild = flattenTemplate({
      child: childVariant,
      parent: templateToFlatten,
      isMappingTemplate: false,
    })

    flattened.set(templateId, flattenedChild as Enriched<T>)
  } else if (!flattened.has(templateId)) {
    flattened.set(templateId, childVariant)
  }
}

const flattenTemplate = <T extends AbstractTemplateInOut>({
  child,
  parent,
  isMappingTemplate,
}: {
  child: Enriched<T>
  parent: Optional<Enriched<T>>
  isMappingTemplate: boolean
}): Enriched<T> => {
  const parentPropertiesNotInChild = parent?.properties?.filter(
    (parentProperty): parentProperty is NonNullable<typeof parentProperty> =>
      !!parentProperty &&
      !child.properties
        ?.filter(notEmpty)
        .some((childProperty) => childProperty.name === parentProperty.name),
  )
  const flattenedProperties = [
    ...(child.properties ?? []).filter(notEmpty).map((childProperty) => {
      const parentProperty = parent?.properties
        ?.filter(notEmpty)
        .find((parentProperty) => parentProperty.name === childProperty.name)

      return {
        ...childProperty,
        value: childProperty.value
          ? flattenTemplateFieldValueInOut({
              child: childProperty.value,
              parent: parentProperty?.value,
              isMappingTemplate,
            })
          : parentProperty?.value,
      }
    }),
    ...(parentPropertiesNotInChild ?? []),
  ]

  const flattenedDeclaredFunctions = flattenFunctions({
    child: child?.declaredFunctions?.filter(notEmpty),
    parent: parent?.declaredFunctions?.filter(notEmpty),
  })
  const flattenedDynamicFunctions = flattenFunctions({
    child: child?.dynamicFunctions?.filter(notEmpty),
    parent: parent?.dynamicFunctions?.filter(notEmpty),
  })

  let inheritablePropsFromParent = {}

  const typedParent = parent as Optional<AbstractTemplateInOut>

  if (typedParent) {
    if (isSubTemplateInOut(typedParent)) {
      const { selectable, defaultSelected, parent } = typedParent
      inheritablePropsFromParent = {
        ...(selectable ? { selectable } : {}),
        ...(defaultSelected ? { defaultSelected } : {}),
        ...(parent ? { parent } : {}),
      }
    } else if (isLibraryInOut(typedParent) || isTemplateInOut(typedParent)) {
      const { valid } = typedParent

      inheritablePropsFromParent = { ...(valid ? { valid } : {}) }
    }
  }

  return {
    ...inheritablePropsFromParent,
    ...child,
    ...(flattenedProperties.length ? { properties: flattenedProperties } : {}),
    ...(flattenedDeclaredFunctions?.length
      ? { declaredFunctions: flattenedDeclaredFunctions }
      : {}),
    ...(flattenedDynamicFunctions?.length
      ? { dynamicFunctions: flattenedDynamicFunctions }
      : {}),
  } as Enriched<T>
}

const flattenTemplateFieldValueInOut = <T extends TemplateFieldValueInOut>({
  child,
  parent,
  isMappingTemplate,
}: {
  child: Enriched<T>
  parent: Optional<Enriched<T>>
  isMappingTemplate: boolean
}) => {
  if (isSimpleValueSetupInOut(child as T)) {
    return flattenSimpleValueSetup({
      parent,
      child,
    })
  } else if (isSubTemplateValueSetupInOut(child as T)) {
    return flattenSubTemplateWithOriginValueSetup({
      parent,
      child,
    })
  } else if (isTemplateValueSetupInOut(child as T)) {
    return flattenTemplateValueSetup({
      child,
      parent,
      isMappingTemplate,
    })
  } else if (isConfigurationExpressionValueInOut(child as T)) {
    return mergeFields({
      child,
      parent,
    })
  } else {
    throw new Error(
      `Unsupported field type encountered during flattening ${child}`,
    )
  }
}

export const flattenHierarchy = <T extends AbstractTemplateInOut>(
  hierarchyTemplates: Enriched<T>[],
  isMappingTemplate: boolean,
) => {
  const result = hierarchyTemplates.reduceRight((child, parent) =>
    flattenTemplate({ child, parent, isMappingTemplate }),
  )

  // This last flattening is required for local inheritance flattening
  return flattenTemplate({ child: result, parent: null, isMappingTemplate })
}
